import React, { useCallback, useEffect, useState } from 'react';
import {
  CalendarClock,
  CheckCircle2,
  Clock3,
  Eye,
  MapPin,
  MessageSquare,
  Search,
  Video,
  UsersRound
} from 'lucide-react';
import { adminAPI } from '../../services/api';
import {
  DetailItem,
  DetailModalShell,
  EmptyState,
  ErrorBanner,
  MetricCard,
  PageSkeleton,
  StatusBadge,
  extractError,
  formatDateTime,
  formatNumber,
  inputClass,
  labelize
} from './hiringUi';

const windowOptions = [
  { value: 'all', label: 'All interviews' },
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'today', label: 'Today' },
  { value: 'needs_feedback', label: 'Needs feedback' },
  { value: 'past', label: 'Past' }
];

const statusOptions = [
  { value: 'all', label: 'All statuses' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'candidate_confirmed', label: 'Candidate confirmed' },
  { value: 'employer_confirmed', label: 'Employer confirmed' },
  { value: 'reschedule_requested', label: 'Reschedule requested' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'candidate_declined', label: 'Candidate declined' },
  { value: 'employer_cancelled', label: 'Employer cancelled' },
  { value: 'no_show', label: 'No show' }
];

const typeOptions = [
  { value: 'all', label: 'All formats' },
  { value: 'video_call', label: 'Video' },
  { value: 'phone', label: 'Phone' },
  { value: 'in_person', label: 'In person' }
];

const sortOptions = [
  { value: 'scheduled_at', label: 'Scheduled time' },
  { value: 'created_at', label: 'Created' },
  { value: 'updated_at', label: 'Updated' },
  { value: 'status', label: 'Status' },
  { value: 'candidate', label: 'Candidate' },
  { value: 'employer', label: 'Employer' }
];

function InterviewDetails({ detail }) {
  const interview = detail?.interview;
  if (!interview) return null;
  return (
    <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_340px]">
      <div className="space-y-5">
        <section className="rounded-[24px] border border-slate-100 bg-white">
          <div className="border-b border-slate-100 px-4 py-3">
            <h3 className="font-semibold text-slate-900">Interview</h3>
          </div>
          <div className="grid gap-3 p-4 sm:grid-cols-2">
            <DetailItem label="Status" value={labelize(interview.status)} />
            <DetailItem label="Result" value={labelize(interview.resultStatus)} />
            <DetailItem label="Scheduled" value={formatDateTime(interview.scheduledDate)} />
            <DetailItem label="Duration" value={`${formatNumber(interview.durationMinutes)} minutes`} />
            <DetailItem label="Format" value={labelize(interview.type)} />
            <DetailItem label="Platform" value={interview.platform} />
            <DetailItem label="Meeting link" value={interview.meetingLink} />
            <DetailItem label="Location" value={interview.location} />
            <DetailItem label="Timezone" value={interview.timezone} />
            <DetailItem label="Employer decision due" value={formatDateTime(interview.employerDecisionDueAt)} />
          </div>
        </section>

        <section className="rounded-[24px] border border-slate-100 bg-white">
          <div className="border-b border-slate-100 px-4 py-3">
            <h3 className="font-semibold text-slate-900">Notes and feedback</h3>
          </div>
          <div className="grid gap-3 p-4">
            <DetailItem label="Instructions" value={interview.instructions} />
            <DetailItem label="Candidate message" value={interview.candidateMessage} />
            <DetailItem label="Interviewer notes" value={interview.interviewerNotes} />
            <DetailItem label="Candidate-visible feedback" value={JSON.stringify(interview.candidateVisibleFeedback || {}, null, 2)} />
            <DetailItem label="Candidate feedback" value={JSON.stringify(interview.candidateSubmittedFeedback || {}, null, 2)} />
            <DetailItem label="Employer result" value={JSON.stringify(interview.employerResult || {}, null, 2)} />
          </div>
        </section>
      </div>

      <aside className="space-y-5">
        <section className="rounded-[24px] border border-slate-100 bg-slate-50 p-4">
          <h3 className="font-semibold text-slate-900">Candidate</h3>
          <div className="mt-3 space-y-3">
            <DetailItem label="Name" value={interview.candidate?.name} />
            <DetailItem label="Email" value={interview.candidate?.email} />
            <DetailItem label="Phone" value={interview.candidate?.phone} />
            <DetailItem label="Profile readiness" value={`${Math.round(interview.candidate?.profileCompletionPercentage || 0)}%`} />
          </div>
        </section>
        <section className="rounded-[24px] border border-slate-100 bg-slate-50 p-4">
          <h3 className="font-semibold text-slate-900">Role</h3>
          <div className="mt-3 space-y-3">
            <DetailItem label="Job" value={interview.job?.title} />
            <DetailItem label="Employer" value={interview.employer?.companyName} />
            <DetailItem label="Match score" value={interview.match?.score === null ? null : `${Math.round(interview.match?.score || 0)}%`} />
            <DetailItem label="Match status" value={labelize(interview.match?.status)} />
          </div>
        </section>
      </aside>
    </div>
  );
}

function InterviewsListPage({ defaultWindow = 'all' }) {
  const [interviews, setInterviews] = useState([]);
  const [stats, setStats] = useState({});
  const [pagination, setPagination] = useState({ total: 0, limit: 25, offset: 0, hasMore: false });
  const [searchInput, setSearchInput] = useState('');
  const [filters, setFilters] = useState({
    search: '',
    window: defaultWindow,
    status: 'all',
    type: 'all',
    sortBy: 'scheduled_at',
    sortOrder: defaultWindow === 'past' ? 'DESC' : 'ASC'
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadInterviews = useCallback(async ({ offset = 0, quiet = false } = {}) => {
    if (quiet) setRefreshing(true);
    else setLoading(true);
    setError('');
    try {
      const response = await adminAPI.getAdminInterviews({ ...filters, limit: pagination.limit, offset });
      setInterviews(response.interviews || []);
      setStats(response.stats || {});
      setPagination(response.pagination || { total: 0, limit: pagination.limit, offset, hasMore: false });
    } catch (loadError) {
      setError(extractError(loadError, 'Interviews could not be loaded.'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [filters, pagination.limit]);

  useEffect(() => {
    loadInterviews({ offset: 0 });
  }, [loadInterviews]);

  const openDetails = async (interview) => {
    setSelected(interview);
    setDetail(null);
    setDetailLoading(true);
    try {
      setDetail(await adminAPI.getAdminInterview(interview.id));
    } catch (detailError) {
      setError(extractError(detailError, 'Interview details could not be loaded.'));
    } finally {
      setDetailLoading(false);
    }
  };

  const resetFilters = () => {
    setSearchInput('');
    setFilters({
      search: '',
      window: defaultWindow,
      status: 'all',
      type: 'all',
      sortBy: 'scheduled_at',
      sortOrder: defaultWindow === 'past' ? 'DESC' : 'ASC'
    });
  };

  const submitSearch = (event) => {
    event.preventDefault();
    setFilters((current) => ({ ...current, search: searchInput.trim() }));
  };

  if (loading) return <PageSkeleton />;

  return (
    <div className="min-h-full bg-[#f8f9fa] px-4 pb-10 pt-20 sm:px-6 lg:px-8 lg:pt-8">
      <div className="mx-auto max-w-[1400px] space-y-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h1 className="mt-2 text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">Interview operations</h1>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-500">
              Platform-wide interview visibility across candidate confirmations, employer confirmations, meeting details, feedback status, and decision deadlines.
            </p>
          </div>
          
        </div>

        <ErrorBanner message={error} />

        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
          <MetricCard icon={CalendarClock} label="Interviews" value={formatNumber(stats.total)} detail={`${formatNumber(pagination.total)} matching filters`} />
          <MetricCard icon={Clock3} label="Upcoming" value={formatNumber(stats.upcoming)} detail={`${formatNumber(stats.today)} today`} />
          <MetricCard icon={MessageSquare} label="Needs feedback" value={formatNumber(stats.needsFeedback)} detail="Completed without result" />
          <MetricCard icon={CheckCircle2} label="Completed" value={formatNumber(stats.completed)} detail="All time" />
          <MetricCard icon={UsersRound} label="Loaded rows" value={formatNumber(interviews.length)} detail="Current page" />
        </div>

        <section className="overflow-hidden rounded-[24px] border border-slate-100 bg-white shadow-sm">
          <div className="border-b border-slate-100 p-4 sm:p-5">
            <form onSubmit={submitSearch} className="flex min-w-0 items-center gap-2 overflow-x-auto pb-1">
              <label className="relative block min-w-[280px] flex-[1_0_320px]">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  className={`${inputClass} w-full pl-9`}
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  placeholder="Search candidate, company, role, platform..."
                />
              </label>
              <select className={`${inputClass} w-[150px] shrink-0`} value={filters.window} onChange={(event) => setFilters((current) => ({ ...current, window: event.target.value }))}>
                {windowOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
              <select className={`${inputClass} w-[155px] shrink-0`} value={filters.status} onChange={(event) => setFilters((current) => ({ ...current, status: event.target.value }))}>
                {statusOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
              <select className={`${inputClass} w-[155px] shrink-0`} value={filters.type} onChange={(event) => setFilters((current) => ({ ...current, type: event.target.value }))}>
                {typeOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
              <select className={`${inputClass} w-[165px] shrink-0`} value={filters.sortBy} onChange={(event) => setFilters((current) => ({ ...current, sortBy: event.target.value }))}>
                {sortOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
              <button type="submit" className="inline-flex h-10 shrink-0 items-center justify-center rounded-lg bg-slate-950 px-4 text-sm font-semibold text-white transition hover:bg-slate-800">
                Search
              </button>
            </form>
          </div>

          {!interviews.length ? (
            <div className="p-5">
              <EmptyState title="No interviews found" copy="Adjust search or filters to review more interview activity." icon={Video} onReset={resetFilters} />
            </div>
          ) : (
            <>
              <div className="hidden overflow-x-auto xl:block">
                <table className="min-w-[1260px] divide-y divide-slate-100">
                  <thead className="bg-slate-50/50">
                    <tr className="text-[10px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100">
                      <th className="px-6 py-4">Candidate</th>
                      <th className="px-6 py-4">Role</th>
                      <th className="px-6 py-4">Schedule</th>
                      <th className="px-6 py-4">Format</th>
                      <th className="px-6 py-4">Status</th>
                      <th className="px-6 py-4">Decision</th>
                      <th className="px-6 py-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {interviews.map((interview) => (
                      <tr key={interview.id} className="align-top transition hover:bg-slate-50/80">
                        <td className="px-6 py-4">
                          <p className="font-semibold text-slate-900">{interview.candidate?.name || 'Candidate'}</p>
                          <p className="mt-1 text-sm text-slate-500">{interview.candidate?.email || 'No email'}</p>
                        </td>
                        <td className="px-6 py-4">
                          <p className="font-medium text-slate-800">{interview.job?.title || 'Untitled role'}</p>
                          <p className="mt-1 text-sm text-slate-500">{interview.employer?.companyName || 'Employer not set'}</p>
                          <p className="mt-1 text-xs text-slate-500">{interview.match?.score === null ? 'No score' : `${Math.round(interview.match?.score || 0)}% match`}</p>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm font-semibold text-slate-900">{formatDateTime(interview.scheduledDate)}</p>
                          <p className="mt-1 text-xs text-slate-500">{formatNumber(interview.durationMinutes)} min · {interview.timezone || 'UTC'}</p>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm text-slate-700">{labelize(interview.type)}</p>
                          <p className="mt-1 text-xs text-slate-500">{interview.platform || interview.location || 'Not set'}</p>
                        </td>
                        <td className="px-6 py-4">
                          <StatusBadge status={interview.status} />
                          <p className="mt-2 text-xs text-slate-500">
                            Candidate {interview.candidateConfirmed ? 'confirmed' : 'pending'} · Employer {interview.employerConfirmed ? 'confirmed' : 'pending'}
                          </p>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm text-slate-700">{labelize(interview.resultStatus)}</p>
                          <p className="mt-1 text-xs text-slate-500">{formatDateTime(interview.employerDecisionDueAt)}</p>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <button type="button" onClick={() => openDetails(interview)} className="w-9 h-9 flex items-center justify-center rounded-xl bg-slate-50 text-slate-500 hover:text-blue-600 hover:bg-blue-50 transition-colors shadow-sm border border-slate-100/50" title="View Details">
                            <Eye className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="grid gap-3 p-4 xl:hidden">
                {interviews.map((interview) => (
                  <article key={interview.id} className="rounded-[24px] border border-slate-100 bg-white p-4 shadow-sm">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h2 className="truncate font-semibold text-slate-900">{interview.candidate?.name || 'Candidate'}</h2>
                        <p className="mt-1 text-sm text-slate-500">{interview.job?.title || 'Untitled role'}</p>
                      </div>
                      <StatusBadge status={interview.status} />
                    </div>
                    <div className="mt-4 grid gap-3 sm:grid-cols-2">
                      <p className="flex items-center gap-2 text-sm text-slate-600"><CalendarClock className="h-4 w-4 text-slate-400" /> {formatDateTime(interview.scheduledDate)}</p>
                      <p className="flex items-center gap-2 text-sm text-slate-600"><Video className="h-4 w-4 text-slate-400" /> {labelize(interview.type)}</p>
                      <p className="flex items-center gap-2 text-sm text-slate-600"><MapPin className="h-4 w-4 text-slate-400" /> {interview.platform || interview.location || 'Not set'}</p>
                      <p className="flex items-center gap-2 text-sm text-slate-600"><MessageSquare className="h-4 w-4 text-slate-400" /> {labelize(interview.resultStatus)}</p>
                    </div>
                    <div className="mt-4 flex justify-end">
                      <button type="button" onClick={() => openDetails(interview)} className="w-9 h-9 flex items-center justify-center rounded-xl bg-slate-50 text-slate-500 hover:text-blue-600 hover:bg-blue-50 transition-colors shadow-sm border border-slate-100/50" title="View Details">
                        <Eye className="w-4 h-4" />
                      </button>
                    </div>
                  </article>
                ))}
              </div>

              <div className="flex flex-col gap-3 border-t border-slate-100 bg-slate-50/70 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-slate-500">
                  Showing {formatNumber(pagination.offset + 1)} - {formatNumber(pagination.offset + interviews.length)} of {formatNumber(pagination.total)}
                </p>
                <div className="flex gap-2">
                  <button type="button" disabled={pagination.offset <= 0 || refreshing} onClick={() => loadInterviews({ offset: Math.max(0, pagination.offset - pagination.limit), quiet: true })} className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50">
                    Previous
                  </button>
                  <button type="button" disabled={!pagination.hasMore || refreshing} onClick={() => loadInterviews({ offset: pagination.offset + pagination.limit, quiet: true })} className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50">
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </section>
      </div>

      {selected && (
        <DetailModalShell
          title={selected.title || 'Interview'}
          subtitle={`${selected.candidate?.name || 'Candidate'} · ${selected.job?.title || 'Role'}`}
          loading={detailLoading}
          onClose={() => {
            setSelected(null);
            setDetail(null);
          }}
        >
          <InterviewDetails detail={detail} />
        </DetailModalShell>
      )}
    </div>
  );
}

export default InterviewsListPage;
